export async function sendEmailNotification({ to, templateKey, variables }: {
  to: string,
  templateKey: string,
  variables: Record<string, any>
}) {
  // Use the provided production backend URL by default, allow override via env
        const BACKEND_URL = process.env.REACT_APP_EMAIL_BACKEND_URL || 'https://approviav1andemail-production.up.railway.app';
  
  console.log('Sending email notification:', { to, templateKey, variables, BACKEND_URL });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
  
  try {
    const response = await fetch(`${BACKEND_URL}/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, templateKey, variables }),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);

  let json: any;
  try {
    json = await response.json();
    console.log('Email backend response:', { status: response.status, json });
  } catch (e) {
    console.error('Failed to parse email backend response:', e);
    throw new Error(`Failed to parse email backend response: ${(e as Error).message}`);
  }

  if (!response.ok || (json && json.error)) {
    const msg = json?.error || `Email backend responded with status ${response.status}`;
    console.error('Email backend error:', msg);
    throw new Error(msg);
  }

  return json;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('Email backend request timed out after 10 seconds');
      throw new Error('Email backend request timed out. The service might be starting up.');
    }
    throw error;
  }
}

// Enhanced email service with specific functions for different triggers
export const EmailService = {
  // Send welcome email to new users
  sendWelcomeEmail: async (to: string, userName: string) => {
    return sendEmailNotification({
      to,
      templateKey: 'welcome',
      variables: { userName }
    });
  },

  // Send review assignment email to reviewer
  sendReviewAssignedEmail: async (to: string, variables: {
    reviewerName: string,
    reviewUrl: string,
    reviewTitle?: string,
    creatorName?: string,
    creatorEmail?: string,
    deadline?: string,
    comment?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'review-assigned',
      variables
    });
  },

  // Send changes needed email to creator
  sendChangesNeededEmail: async (to: string, variables: {
    reviewerName: string,
    reviewUrl: string,
    comments?: string,
    annotations?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'changes-needed-creator',
      variables
    });
  },

  // Send changes needed confirmation to reviewer
  sendChangesNeededConfirmationEmail: async (to: string, variables: {
    creatorName: string,
    reviewUrl: string,
    comments?: string,
    annotations?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'changes-needed-reviewer',
      variables
    });
  },

  // Send approval email to creator
  sendApprovalEmail: async (to: string, variables: {
    reviewerName: string,
    reviewUrl: string,
    approvalDate?: string,
    comments?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'approved-creator',
      variables
    });
  },

  // Send approval confirmation to reviewer
  sendApprovalConfirmationEmail: async (to: string, variables: {
    creatorName: string,
    reviewUrl: string,
    approvalDate?: string,
    comments?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'approved-reviewer',
      variables
    });
  },

  // Send review reminder email
  sendReviewReminderEmail: async (to: string, variables: {
    reviewUrl: string,
    deadline?: string,
    comment?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'review-reminder',
      variables
    });
  },

  // Send review overdue email
  sendReviewOverdueEmail: async (to: string, variables: {
    reviewUrl: string,
    deadline?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'review-overdue',
      variables
    });
  },

  // Send review status update email
  sendReviewStatusUpdateEmail: async (to: string, variables: {
    statusTitle: string,
    statusMessage: string,
    reviewUrl: string,
    reviewTitle: string,
    reviewerName: string,
    deadline?: string,
    statusBadge?: boolean,
    statusBadgeBg?: string,
    statusBadgeBorder?: string,
    statusBadgeColor?: string,
    statusIcon?: string,
    statusBadgeText?: string,
    statusDescription?: string
  }) => {
    return sendEmailNotification({
      to,
      templateKey: 'review-status-update',
      variables
    });
  },

  // Send password reset email
  sendPasswordResetEmail: async (to: string, resetUrl: string) => {
    return sendEmailNotification({
      to,
      templateKey: 'password-reset',
      variables: { resetUrl }
    });
  },

  // Send review completed email
  sendReviewCompletedEmail: async (to: string, reviewUrl: string) => {
    return sendEmailNotification({
      to,
      templateKey: 'review-completed',
      variables: { reviewUrl }
    });
  }
}; 